import type { Information, InformationField, SheetMath } from "@pdfking/imposition"
import { VirtualFileType } from "@pdfking/pdfengine"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { Sequence } from "@pdfking/tfunc"
import { DeleteDuplicates, Items, Length, SortBy, Values } from "@pdfking/tfunc"

import type { FileCollectionStore, FileStore, PageCount } from "#public"

import { useTranslation, useTranslationTyped } from "../i18n/useTranslation"
import { Pdfmessage } from "./Pdfmessage"

// the process families, as split names them: before any spot colorant
const PROCESS = ["rgb", "cmyk", "gray", "lab"]

// the process families by their labels; a spot colorant reads its own name
const LABEL: Record<string, string> = { cmyk: "CMYK", gray: "Gray", lab: "Lab", rgb: "RGB" }

// the process families a press cannot print as they are
const CONVERTED = ["rgb", "lab"]

const ConvertedColors = (colors: string[]): string[] =>
  colors.filter(color => CONVERTED.includes(color)).map(color => LABEL[color]!)

const Rank = (color: string) => {
  const at = PROCESS.indexOf(color)
  return at < 0 ? PROCESS.length : at
}

// What the files draw with, over the pages their split has reported so far:
// each page's colors joined, once each, the process families first, then the
// spot colorants by name.
const FileColors = (stores: Sequence<Pick<FileStore, "pages">>): string[] =>
  SortBy(
    DeleteDuplicates(
      Array.from(stores).flatMap(store =>
        Values(store.pages).flatMap(page =>
          page.enum === VirtualFileType.BINARY_PAGE_PDF ? (page.colors ?? []) : []
        )
      )
    ),
    color => [Rank(color), color]
  )

// A fact of the sheets math, its name in bold when it has one.
const Field = ({ name, text }: InformationField) => (
  <span className="block">
    {name && <strong>{name}: </strong>}
    <span className="font-mono text-muted-foreground">{text}</span>
  </span>
)

// The foot of a sidebar panel, pushed down past whatever space the panel
// leaves: what the job comes to, inline, a name and its value per fact — the
// color spaces the files draw with first, a spot per badge, then the
// imposition's fields — and under it what to be warned about: the colors the
// press cannot print as they are, then the imposition's own warnings. Last,
// for staff, the sheets math, a numbered item per component.
export const PanelInformations = CreateRenderFunction({
  props: {
    fields: [] as InformationField[],
    informations: [] as Information[],
    page_counts: [] as PageCount[],
    sheet_math: [] as SheetMath[],
    store: undefined as unknown as FileCollectionStore,
  },
  render() {
    const pdfview = useTranslationTyped("pdfview")
    const entities = useTranslation("entities")
    // what the files draw with, as far as their splits have got, the part of
    // it the press will have to convert, and the files that draw with it
    const colors = FileColors(Values(this.store.files))
    const converted = ConvertedColors(colors)
    const converted_names = Items(this.store.files)
      .filter(([, entry]) => ConvertedColors(FileColors([entry])).length)
      .map(([name]) => name)
    // the destinations whose files select other than the pages they print —
    // nothing to compare before a file is uploaded
    const mismatched = Length(this.store.files)
      ? this.page_counts.filter(({ expected, selected }) => expected !== selected)
      : []
    if (
      !colors.length &&
      !this.fields.length &&
      !mismatched.length &&
      !this.informations.length &&
      !this.sheet_math.length
    )
      return undefined
    return (
      <div className="mt-auto flex flex-col gap-2 pb-2">
        {!!converted.length && (
          <Pdfmessage
            content={[
              pdfview.COLOR_WARNING({
                colors: converted.join(", "),
                names: converted_names.join(", "),
              }),
            ]}
            level="danger"
          />
        )}
        {mismatched.map(({ expected, name, selected }) => (
          <Pdfmessage
            content={[
              pdfview.PAGE_COUNT_WARNING({
                expected,
                name: entities[name]?.() ?? name,
                selected,
              }),
            ]}
            key={name}
            level="warning"
          />
        ))}
        {this.informations.map((item, index) => (
          <Pdfmessage key={index} {...item} />
        ))}
        {/* Under the warnings, text, a name and its value per fact: the color
            spaces on a line of their own, then the imposition's fields,
            bullets between them. */}
        {(!!colors.length || !!this.fields.length) && (
          <p className="m-0 px-2 text-xs">
            {!!colors.length && (
              <span className="block">
                <span className="text-muted-foreground">{pdfview.COLORS()}</span>{" "}
                {colors.map(color => LABEL[color] ?? color).join(" • ")}
              </span>
            )}
            {this.fields.map(({ name, text }, index) => (
              <span key={name}>
                {index ? " • " : ""}
                <span className="text-muted-foreground">{name}</span> {text}
              </span>
            ))}
          </p>
        )}
        {!!this.sheet_math.length && (
          <ol className="m-0 list-decimal pr-2 pl-6 text-xs">
            {this.sheet_math.map(({ steps, ...field }, index) => (
              <li key={index}>
                <Field {...field} />
                <ol className="m-0 list-decimal pl-5">
                  {steps.map((step, s) => (
                    <li key={s}>
                      <Field {...step} />
                    </li>
                  ))}
                </ol>
              </li>
            ))}
          </ol>
        )}
      </div>
    )
  },
})
